import type {
  ActiveKenmerkSearch,
  KenmerkSearchMatchWithHighlight,
  KenmerkSearchNavigation,
} from '../../../../types';

export type KenmerkSearchState = {
  openGroupName: string | null;
  activeKenmerkSearch: ActiveKenmerkSearch | null;
  kenmerkSearchNavigation: KenmerkSearchNavigation | null;
};

export const initialKenmerkSearchState: KenmerkSearchState = {
  openGroupName: null,
  activeKenmerkSearch: null,
  kenmerkSearchNavigation: null,
};

type KenmerkSearchAction =
  | {
      type: 'SEARCH';
      query: string;
      matches: Array<KenmerkSearchMatchWithHighlight>;
    }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'NAVIGATE'; delta: -1 | 1 }
  | { type: 'COLLAPSE_GROUP' }
  | { type: 'SET_OPEN_GROUP'; groupName: string | null };

const wrapIndex = (index: number, length: number, delta: number) =>
  (index + delta + length) % length;

const toActiveKenmerkSearch = (
  match: KenmerkSearchMatchWithHighlight,
): ActiveKenmerkSearch => ({
  kenmerkId: match.kenmerk.id,
  highlightSubstring: match.highlightSubstring,
});

export const kenmerkSearchReducer = (
  state: KenmerkSearchState,
  action: KenmerkSearchAction,
): KenmerkSearchState => {
  switch (action.type) {
    case 'SET_OPEN_GROUP':
      return { ...state, openGroupName: action.groupName };
    case 'COLLAPSE_GROUP':
      return {
        ...state,
        openGroupName: null,
        activeKenmerkSearch: null,
      };
    case 'CLEAR_SEARCH':
      return {
        ...state,
        kenmerkSearchNavigation: null,
        activeKenmerkSearch: null,
      };
    case 'SEARCH': {
      const kenmerkSearchNavigation: KenmerkSearchNavigation = {
        query: action.query,
        matches: action.matches,
        currentIndex: 0,
      };
      const firstMatch = action.matches[0];

      if (!firstMatch) {
        return {
          ...state,
          kenmerkSearchNavigation,
          activeKenmerkSearch: null,
        };
      }

      return {
        ...state,
        kenmerkSearchNavigation,
        openGroupName: firstMatch.groupName,
        activeKenmerkSearch: toActiveKenmerkSearch(firstMatch),
      };
    }
    case 'NAVIGATE': {
      const navigation = state.kenmerkSearchNavigation;

      if (!navigation?.matches.length) return state;

      const nextIndex = wrapIndex(
        navigation.currentIndex,
        navigation.matches.length,
        action.delta,
      );
      const match = navigation.matches[nextIndex];

      return {
        ...state,
        kenmerkSearchNavigation: {
          ...navigation,
          currentIndex: nextIndex,
        },
        openGroupName: match.groupName,
        activeKenmerkSearch: toActiveKenmerkSearch(match),
      };
    }
    default:
      return state;
  }
};
